import {Page, PageSeo, PageType} from '../../components/common/container';
import {Trans, useTranslation} from 'next-i18next';
import {Prose, SectionTitle, Title} from '../../components/common/text';
import {GetStaticProps} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import {Box, Typography} from '@mui/material';
import Link from 'next/link';
import {CONTACT, KUNEKUNE_INFO} from '../../constants';
import {Warning} from '../../components/common/frame';

export const getStaticProps: GetStaticProps<{}> = async (context) => {
    const translation = await serverSideTranslations(context.locale, ['common', 'cgv']);
    return {
        props: translation,
        revalidate: 86400, // 1 jour
    };
};

export default function Cgu() {
    const {t} = useTranslation('cgv');

    return (
        <Page>
            <PageSeo page={PageType.CGV} />
            <Title>{t('title')}</Title>
            <Box
                sx={{
                    maxWidth: 550,
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    paddingLeft: 4,
                    paddingRight: 4,
                }}
            >
                <CgvUpdated />
                <CgvFirstArticle />
                <CgvSecondArticle />
                <CgvThirdArticle />
                <CgvFourthArticle />
                <CgvFifthArticle />
                <CgvSixthArticle />
                <CgvSeventhArticle />
                <CgvEighthArticle />
                <CgvNinthArticle />
                <CgvTenthArticle />
                <CgvEleventhArticle />
                <CgvTwelfthArticle />
                <CgvThirteenthArticle />
                <CgvFourteenthArticle />
                <CgvFifteenthArticle />
            </Box>
            <AnnexOne />
            <AnnexTwo />
        </Page>
    );
}

function CgvUpdated() {
    const {t} = useTranslation('cgv');

    return (
        <Typography variant="caption">
            {t('updated', {val: new Date(Date.UTC(2023, 6, 30, 0, 0, 0))})}
        </Typography>
    );
}

function CgvFirstArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>1. {t('article.1.title')}</SectionTitle>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.1.content.1"
                    values={{
                        companyId: KUNEKUNE_INFO.ID,
                        companyName: KUNEKUNE_INFO.NAME,
                        companyAddress: KUNEKUNE_INFO.ADDRESS,
                        companyCapital: KUNEKUNE_INFO.CAPITAL,
                        companyRegisteredUnder: KUNEKUNE_INFO.REGISTRAR,
                    }}
                    components={{kuneKuneLink: <Link href={'/'} />}}
                />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.1.content.2" />
            </Prose>
            <Prose>{t('article.1.content.3')}</Prose>
            <ul>
                <li>
                    {t('article.1.content.contact.email', {
                        email: CONTACT.EMAIL,
                    })}
                </li>
                <li>
                    {t('article.1.content.contact.phone', {
                        phone: CONTACT.PHONE,
                    })}
                </li>
            </ul>
        </>
    );
}

function CgvSecondArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>2. {t('article.2.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.2.content.1" />
            </Prose>
            <Prose>{t('article.2.content.2')}</Prose>
            <Prose>
                <Trans t={t} i18nKey="article.2.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.2.content.4" />
            </Prose>
            <Prose>{t('article.2.content.5')}</Prose>
            <Warning>
                <Trans t={t} i18nKey="article.2.content.langWarning" />
            </Warning>
        </>
    );
}

function CgvThirdArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>3. {t('article.3.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.3.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.3.content.2" />
            </Prose>
        </>
    );
}

function CgvFourthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>4. {t('article.4.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.4" />
            </Prose>
            <ul>
                <li>{t('article.4.content.4-steps.1')}</li>
                <li>{t('article.4.content.4-steps.2')}</li>
                <li>{t('article.4.content.4-steps.3')}</li>
                <li>{t('article.4.content.4-steps.4')}</li>
            </ul>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.5" />
            </Prose>
            <ul>
                <li>{t('article.4.content.5-steps.1')}</li>
                <li>{t('article.4.content.5-steps.2')}</li>
                <li>{t('article.4.content.5-steps.3')}</li>
                <li>{t('article.4.content.5-steps.4')}</li>
            </ul>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.6" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.4.content.7" />
            </Prose>
        </>
    );
}

function CgvFifthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>5. {t('article.5.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.5.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.5.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.5.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.5.content.4" />
            </Prose>
        </>
    );
}

function CgvSixthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>6. {t('article.6.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.6.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.6.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.6.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.6.content.4" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.6.content.5" />
            </Prose>
        </>
    );
}

function CgvSeventhArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>7. {t('article.7.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.7.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.7.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.7.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.7.content.4" />
            </Prose>
        </>
    );
}

function CgvEighthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>8. {t('article.8.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.8.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.8.content.2" />
            </Prose>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.8.content.3"
                    values={{
                        email: CONTACT.EMAIL,
                    }}
                />
            </Prose>
        </>
    );
}

function CgvNinthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>9. {t('article.9.title')}</SectionTitle>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.9.content"
                    components={{
                        privacyPolicyLink: <Link href={'/info/privacy-policy'} />,
                    }}
                />
            </Prose>
        </>
    );
}

function CgvTenthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>10. {t('article.10.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.10.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.10.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.10.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.10.content.4" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.10.content.5" />
            </Prose>
        </>
    );
}

function CgvEleventhArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>11. {t('article.11.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.11.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.11.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.11.content.3" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.11.content.4" />
            </Prose>
        </>
    );
}

function CgvTwelfthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>12. {t('article.12.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.12.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.12.content.2" />
            </Prose>
        </>
    );
}

function CgvThirteenthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>13. {t('article.13.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.13.content" />
            </Prose>
        </>
    );
}

function CgvFourteenthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>14. {t('article.14.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.14.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.14.content.2" />
            </Prose>
        </>
    );
}

function CgvFifteenthArticle() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <SectionTitle>15. {t('article.15.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.15.content.1" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.15.content.2" />
            </Prose>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.15.content.3"
                    components={{
                        mediationLink: (
                            <Link href="https://www.mediateurfevad.fr">
                                Médiateur de la Fédération professionnelle du e-commerce et de
                                la vente à distance (FEVAD)
                            </Link>
                        ),
                    }}
                />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.15.content.4" />
            </Prose>
        </>
    );
}

function AnnexOne() {
    return (
        <>
            <ConformityWarranties />
            <HiddenDefectsWarranties />
        </>
    );
}

function ConformityWarranties() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <Title style={{textAlign: 'center'}}>
                {t('annex.1.conformity-warranties.title')}
            </Title>
            <Box
                sx={{
                    maxWidth: 550,
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    paddingLeft: 4,
                    paddingRight: 4,
                }}
            >
                <SectionTitle>{t('annex.1.conformity-warranties.L.217-4.title')}</SectionTitle>
                <Prose>{t('annex.1.conformity-warranties.L.217-4.content.1')}</Prose>
                <ol>
                    <li>{t('annex.1.conformity-warranties.L.217-4.content.1-list.1')}</li>
                    <li>{t('annex.1.conformity-warranties.L.217-4.content.1-list.2')}</li>
                    <li>{t('annex.1.conformity-warranties.L.217-4.content.1-list.3')}</li>
                    <li>{t('annex.1.conformity-warranties.L.217-4.content.1-list.4')}</li>
                </ol>

                <SectionTitle>{t('annex.1.conformity-warranties.L.217-5.title')}</SectionTitle>
                <ol type="I">
                    <li>
                        <p>{t('annex.1.conformity-warranties.L.217-5.content.1.content')}</p>
                        <ol>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.1.list.1')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.1.list.2')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.1.list.3')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.1.list.4')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.1.list.5')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.1.list.6')}
                            </li>
                        </ol>
                    </li>
                    <li>
                        <p>{t('annex.1.conformity-warranties.L.217-5.content.2.content')}</p>
                        <ol>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.2.list.1')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.2.list.2')}
                            </li>
                            <li>
                                {t('annex.1.conformity-warranties.L.217-5.content.2.list.3')}
                            </li>
                        </ol>
                    </li>
                    <li>{t('annex.1.conformity-warranties.L.217-5.content.3')}</li>
                </ol>

                <SectionTitle>
                    {t('annex.1.conformity-warranties.L.217-12.title')}
                </SectionTitle>
                <Prose>{t('annex.1.conformity-warranties.L.217-12.content.1')}</Prose>
                <ol>
                    <li>{t('annex.1.conformity-warranties.L.217-12.content.1-list.1')}</li>
                    <li>{t('annex.1.conformity-warranties.L.217-12.content.1-list.2')}</li>
                    <li>{t('annex.1.conformity-warranties.L.217-12.content.1-list.3')}</li>
                </ol>
                <Prose>{t('annex.1.conformity-warranties.L.217-12.content.2')}</Prose>
                <Prose>{t('annex.1.conformity-warranties.L.217-12.content.3')}</Prose>
                <Prose>{t('annex.1.conformity-warranties.L.217-12.content.4')}</Prose>
            </Box>
        </>
    );
}

function HiddenDefectsWarranties() {
    const {t} = useTranslation('cgv');

    return (
        <>
            <Title style={{textAlign: 'center'}}>
                {t('annex.1.hidden-defects-warranties.title')}
            </Title>
            <Box
                sx={{
                    maxWidth: 550,
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    paddingLeft: 4,
                    paddingRight: 4,
                }}
            >
                <SectionTitle>
                    {t('annex.1.hidden-defects-warranties.1641.title')}
                </SectionTitle>
                <Prose>{t('annex.1.hidden-defects-warranties.1641.content')}</Prose>

                <SectionTitle>
                    {t('annex.1.hidden-defects-warranties.1644.title')}
                </SectionTitle>
                <Prose>{t('annex.1.hidden-defects-warranties.1644.content')}</Prose>

                <SectionTitle>
                    {t('annex.1.hidden-defects-warranties.1648.title')}
                </SectionTitle>
                <Prose>{t('annex.1.hidden-defects-warranties.1648.content.1')}</Prose>
                <Prose>{t('annex.1.hidden-defects-warranties.1648.content.2')}</Prose>
            </Box>
        </>
    );
}

const FORM_SPACE = 50;

function AnnexTwo() {
    const {t} = useTranslation('cgv');

    return (
        <Box
            sx={{
                paddingLeft: 4,
                paddingRight: 4,
            }}
        >
            <Title style={{textAlign: 'center'}}>{t('annex.2.title')}</Title>
            <Box
                style={{
                    maxWidth: 550,
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    borderStyle: 'solid',
                    borderWidth: 1,
                    padding: 20,
                    borderRadius: 10,
                }}
            >
                <Prose>{t('annex.2.form.1')}</Prose>
                <Box style={{height: FORM_SPACE}} />
                <Prose>{t('annex.2.form.2')}</Prose>
                <Box style={{height: FORM_SPACE}} />
                <Prose>{t('annex.2.form.3')}</Prose>
                <Box style={{height: FORM_SPACE}} />
                <Prose>{t('annex.2.form.4')}</Prose>
                <Box style={{height: FORM_SPACE}} />
                <Prose>{t('annex.2.form.5')}</Prose>
                <Box style={{height: FORM_SPACE}} />
                <Prose>{t('annex.2.form.6')}</Prose>
                <Box style={{height: FORM_SPACE}} />
                <Typography variant="caption">{t('annex.2.notice')}</Typography>
            </Box>
        </Box>
    );
}
